<template lang="pug">
.evento
  .evento__empty-state(v-if="!currentEvent")
    FontAwesomeIcon.evento__empty-ic(:icon="['far','calendar']")
    p Nenhum evento selecionado.
    button.evento__cta(@click="voltar")
      span Ver eventos
      FontAwesomeIcon(:icon="['fas','arrow-right']")

  template(v-else)
    //- ============ CABEÇALHO ============
    header.evento__hero(:class="{ 'evento__hero--banner': eventBanner }")
      .evento__hero-bg(v-if="eventBanner" :style="{ backgroundImage: 'url(' + eventBanner + ')' }")
      .evento__hero-grid
      .evento__hero-glow.evento__hero-glow--blue
      .evento__hero-glow.evento__hero-glow--red
      .evento__hero-inner
        .evento__topbar
          button.evento__back(@click="voltar")
            FontAwesomeIcon(:icon="['fas','arrow-left']")
            span Eventos
          button.evento__edit(v-if="canManage" @click="doEdit")
            FontAwesomeIcon(:icon="['fas','edit']")
            span Editar evento
        p.evento__eyebrow
          span.evento__eyebrow-pip
          | Resumo do evento
        h1.evento__title {{ currentEvent.name }}
        .evento__badges
          span.evento__status(:class="'evento__status--' + statusClass") {{ statusLabel }}
          span.evento__when(:class="'evento__when--' + statusClass" v-if="relativo")
            span.evento__when-dot(v-if="statusClass === 'live'")
            FontAwesomeIcon.evento__when-ic(v-else :icon="['far','clock']")
            | {{ relativo }}
          span.evento__test(v-if="currentEvent.is_test") Teste
        .evento__meta
          span.evento__meta-item(v-if="periodo")
            FontAwesomeIcon.evento__meta-ic(:icon="['far','calendar-alt']")
            | {{ periodo }}
          span.evento__meta-item(v-if="currentEvent.address")
            FontAwesomeIcon.evento__meta-ic(:icon="['fas','map-marker-alt']")
            | {{ currentEvent.address }}
          span.evento__meta-item
            FontAwesomeIcon.evento__meta-ic(:icon="['fas','sitemap']")
            | {{ bracketsGerados ? 'Chaves geradas' : 'Chaves não geradas' }}
        p.evento__desc(v-if="currentEvent.description") {{ currentEvent.description }}

    .evento__wrap
      //- ============ NAVEGAÇÃO ============
      .evento__nav-grid
        button.evento__nav(v-for="l in links" :key="l.name" @click="doNavigate(l.name)")
          .evento__nav-ic
            FontAwesomeIcon(:icon="l.icon")
          .evento__nav-txt
            strong {{ l.label }}
            span {{ l.desc }}
          FontAwesomeIcon.evento__nav-arrow(:icon="['fas','arrow-right']")

      //- Visitante deslogado vê só a navegação acima (Grupos, Chaves, Lutas e
      //- Robôs). Os números de gestão do evento ficam para quem tem conta.
      template(v-if="logado")
        //- ============ RESUMO ============
        h2.evento__section-title Resumo
        .evento__stats
          .evento__stat(
            v-for="c in resumoCards"
            :key="c.label"
            :class="'evento__stat--' + c.tone"
          )
            .evento__stat-ic
              FontAwesomeIcon(:icon="c.icon")
            span.evento__stat-num {{ c.value }}
            span.evento__stat-label {{ c.label }}

        //- ============ DURAÇÃO ESTIMADA (só organização) ============
        template(v-if="duration")
          h2.evento__section-title
            | {{ isRealSchedule ? 'Duração da competição' : 'Duração estimada' }}
            span.evento__dur-tag
              FontAwesomeIcon(:icon="['fas','lock']")
              | Só organização
          .evento__dur-grid
            .evento__dur-card.evento__dur-card--total
              span.evento__dur-value {{ fmtDur(duration.total_minutes) }}
              span.evento__dur-label Total{{ isRealSchedule ? '' : ' (' + duration.arenas + ' arena' + (duration.arenas > 1 ? 's' : '') + ')' }}
            .evento__dur-card
              span.evento__dur-value {{ fmtDur(duration.group_minutes) }}
              span.evento__dur-label Fase de grupos · {{ duration.group_fights }} lutas
            .evento__dur-card
              span.evento__dur-value {{ fmtDur(duration.bracket_minutes) }}
              span.evento__dur-label Chaves · {{ duration.bracket_fights }} lutas
          p.evento__note(v-if="duration.source === 'agenda'")
            | Tempo real da agenda: {{ fmtRange }} ({{ duration.slot_minutes }}min por luta, {{ duration.rest_minutes }}min de descanso por robô).
          p.evento__note(v-else-if="duration.source === 'parcial'")
            | Parcial: a fase já agendada usa os horários reais ({{ fmtRange }}); a outra segue estimada em {{ duration.slot_minutes }}min por luta e {{ duration.arenas }} arena(s).
          p.evento__note(v-else)
            | Estimativa: {{ duration.slot_minutes }}min por luta, {{ duration.rest_minutes }}min de descanso por robô, {{ duration.arenas }} arena(s). Gere a agenda em Chaves para ver o tempo real.

        //- ============ POR CATEGORIA ============
        h2.evento__section-title Por categoria

        .evento__panel(v-if="categorias.length")
          table.evento__table
            thead
              tr
                th Categoria
                th.num Inscritos
                th.num Aprovados
                th.num Lutas previstas
                th.num Lutas realizadas
            tbody
              tr(v-for="cat in categorias" :key="cat.category_id")
                td {{ cat.category_name }}
                td.num {{ cat.robots }}
                td.num {{ cat.approved }}
                td.num {{ plannedFor(cat) }}
                td.num {{ cat.fights_done }}
            tfoot
              tr
                td Total
                td.num {{ totalRobos }}
                td.num {{ totalAprovados }}
                td.num {{ lutasPrevistas }}
                td.num {{ lutasRealizadas }}

        .evento__empty(v-else)
          FontAwesomeIcon.evento__empty-ic(:icon="['fas','layer-group']")
          p Nenhuma categoria cadastrada neste evento.

        p.evento__note(v-if="categorias.length && !bracketsGerados")
          | * Lutas previstas são estimativas de uma chave de dupla eliminação; os valores reais aparecem após gerar as chaves.
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { statusOf, relativeLabel } from '@/utils/eventDate'

export default {
  name: 'EventoDetalhe',
  data: () => ({
    eventBanner: '',
    duration: null,
    logado: false,
    links: [
      { name: 'Groups', label: 'Grupos', desc: 'Fase de grupos e classificação', icon: ['fas', 'layer-group'] },
      { name: 'Keys', label: 'Chaves', desc: 'Brackets e resultados', icon: ['fas', 'sitemap'] },
      { name: 'Fights', label: 'Lutas', desc: 'Programação e arenas', icon: ['fas', 'bolt'] },
      { name: 'Robots', label: 'Robôs', desc: 'Inscritos e inspeção', icon: ['fas', 'robot'] },
      { name: 'Podio', label: 'Pódio', desc: 'Classificação final', icon: ['fas', 'trophy'] }
    ]
  }),
  created () {
    try {
      this.logado = !!localStorage.getItem('token')
    } catch (e) { /* localStorage indisponível: trata como visitante */ }
    this.setEvents().then(() => {
      if (this.getCurrentEventId) {
        this.setEventSummary(this.getCurrentEventId)
        this.loadBanner()
        this.loadDuration()
      }
    })
  },
  computed: {
    ...mapGetters('events', ['getCurrentEvent', 'getCurrentEventId', 'getEventSummary']),
    ...mapGetters('users', ['getUser']),
    currentEvent () {
      return this.getCurrentEvent
    },
    canManage () {
      const roles = this.getUser.roles
      return Array.isArray(roles) && (roles.includes(1) || roles.includes(6))
    },
    // true quando o tempo vem dos horários reais do agendador (não do modelo)
    isRealSchedule () {
      return !!(this.duration && this.duration.source === 'agenda')
    },
    // "01/08 10:30 às 19:30" — janela real da agenda
    fmtRange () {
      const d = this.duration
      if (!d || !d.start_at || !d.end_at) return ''
      const f = (v) => {
        const x = new Date(v)
        if (isNaN(x.getTime())) return ''
        const p = n => String(n).padStart(2, '0')
        // horários da agenda são wall-clock rotulado Z (mesma convenção do
        // schedule_fights): ler em UTC para não deslocar 3h
        return `${p(x.getUTCDate())}/${p(x.getUTCMonth() + 1)} ${p(x.getUTCHours())}:${p(x.getUTCMinutes())}`
      }
      return `${f(d.start_at)} às ${f(d.end_at)}`
    },
    categorias () {
      return this.getEventSummary || []
    },
    totalRobos () {
      return this.categorias.reduce((sum, c) => sum + (c.robots || 0), 0)
    },
    // Aprovados = as duas inspeções, o mesmo critério do chip "Aprovados" da
    // tela de Robôs e da elegibilidade à chave.
    totalAprovados () {
      return this.categorias.reduce((sum, c) => sum + (c.approved || 0), 0)
    },
    numCategorias () {
      return this.categorias.length
    },
    numArenas () {
      return this.categorias.length ? (this.categorias[0].arenas_count || 0) : 0
    },
    lutasRealizadas () {
      return this.categorias.reduce((sum, c) => sum + (c.fights_done || 0), 0)
    },
    lutasPrevistas () {
      return this.categorias.reduce((sum, c) => sum + this.plannedFor(c), 0)
    },
    lutasPendentes () {
      return Math.max(0, this.lutasPrevistas - this.lutasRealizadas)
    },
    bracketsGerados () {
      return this.categorias.some(c => (c.fights_total || 0) > 0)
    },
    resumoCards () {
      return [
        { label: 'Robôs aprovados', value: this.totalAprovados, icon: ['fas', 'robot'], tone: 'blue' },
        { label: 'Robôs inscritos', value: this.totalRobos, icon: ['fas', 'clipboard-list'], tone: 'blue' },
        { label: 'Categorias', value: this.numCategorias, icon: ['fas', 'layer-group'], tone: 'blue' },
        { label: 'Arenas', value: this.numArenas, icon: ['fas', 'vector-square'], tone: 'blue' },
        { label: 'Lutas previstas', value: this.lutasPrevistas, icon: ['fas', 'list-ol'], tone: 'red' },
        { label: 'Lutas realizadas', value: this.lutasRealizadas, icon: ['fas', 'flag-checkered'], tone: 'red' },
        { label: 'Lutas pendentes', value: this.lutasPendentes, icon: ['fas', 'hourglass-half'], tone: 'red' }
      ]
    },
    periodo () {
      if (!this.currentEvent) return ''
      const ini = this.formatDate(this.currentEvent.startat)
      const fim = this.formatDate(this.currentEvent.endat)
      if (ini && fim) return `${ini} a ${fim}`
      return ini || fim
    },
    // Status e texto relativo comparam DIA DE CALENDÁRIO (ver utils/eventDate).
    statusClass () {
      if (!this.currentEvent) return 'idle'
      return statusOf(this.currentEvent)
    },
    statusLabel () {
      return {
        done: 'Encerrado',
        scheduled: 'Agendado',
        live: 'Em andamento',
        idle: '-'
      }[this.statusClass]
    },
    relativo () {
      return relativeLabel(this.currentEvent)
    }
  },
  methods: {
    ...mapActions('events', ['setEvents', 'setEventSummary', 'fetchEventImage', 'fetchEventDuration']),
    loadBanner () {
      this.fetchEventImage(this.getCurrentEventId).then(img => { this.eventBanner = img || '' })
    },
    // O backend só devolve a duração a Admin/Organizador (403 → null p/ o resto),
    // então o card aparece apenas para a organização.
    loadDuration () {
      this.duration = null
      if (!this.logado) return // visitante não vê duração: evita um 401 inútil
      this.fetchEventDuration(this.getCurrentEventId).then(d => { this.duration = d && d.total_minutes != null ? d : null })
    },
    fmtDur (min) {
      if (min == null) return '-'
      const h = Math.floor(min / 60)
      const m = Math.round(min % 60)
      if (h <= 0) return `${m}min`
      return m > 0 ? `${h}h ${m}min` : `${h}h`
    },
    voltar () {
      this.$router.push('/Eventos')
    },
    doEdit () {
      this.$router.push('/EditarEvento')
    },
    doNavigate (name) {
      if (name && name !== this.$route.name) {
        this.$router.push('/' + name)
      }
    },
    // Chave gerada: usa o número real de lutas. Antes disso é estimativa, e a
    // base são os INSCRITOS, não os aprovados: planeja-se para quem se inscreveu,
    // já que a inspeção ainda não aconteceu. (Antes esta conta usava a contagem
    // de aprovados-na-estática, então um evento recém-inscrito previa 0 lutas.)
    plannedFor (cat) {
      if ((cat.fights_total || 0) > 0) return cat.fights_total
      const n = cat.robots || 0
      return n >= 2 ? (n * 2 - 2) : 0
    },
    formatDate (value) {
      if (!value) return ''
      const d = new Date(value)
      if (isNaN(d.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      // dia de calendário gravado como meia-noite UTC: ler em UTC (getters locais
      // devolviam o dia anterior em UTC-3)
      return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`
    }
  },
  watch: {
    getCurrentEventId (newId) {
      this.setEventSummary(newId)
      this.eventBanner = ''
      this.duration = null
      if (newId) {
        this.loadBanner()
        this.loadDuration()
      }
    }
  }
}
</script>

<style lang="sass" scoped>
$blue: #287AED
$blue-dark: #0F4FA8
$red: #DD3434
$red-dark: #B0231F
$green: #2E9E4F
$green-dark: #1F7C3C
$ink: #0B0E14
$muted: #5C6673
$line: rgba(11, 14, 20, 0.10)
$paper: #FFFFFF
$mist: #EEF1F6
$page: #F2F4F8

$display: 'Saira Condensed', 'Lato', sans-serif
$mono: 'Space Mono', 'Lato', monospace

.evento
  color: $ink
  background: $page
  min-height: calc(100vh - 64px)

  *
    box-sizing: border-box

  // ---------- CABEÇALHO ----------
  &__hero
    position: relative
    overflow: hidden
    isolation: isolate
    padding: 28px 0 46px
    background: radial-gradient(circle at 16% 8%, #15233b 0%, $ink 52%, #05070b 100%)
    color: #fff

  &__wrap
    max-width: 1140px
    margin: 0 auto
    padding: 36px 24px 64px

  &__hero-grid
    position: absolute
    inset: 0
    background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
    background-size: 42px 42px
    mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
    -webkit-mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
    z-index: -2

  // Banner do evento como fundo do hero. Um scrim escuro (mais forte à esquerda,
  // onde fica o texto) mantém título/badges legíveis e deixa a arte visível à direita.
  &__hero-bg
    position: absolute
    inset: 0
    z-index: -2
    background-size: cover
    background-position: center
    &::after
      content: ''
      position: absolute
      inset: 0
      background: linear-gradient(90deg, rgba(11, 14, 20, 0.94) 0%, rgba(11, 14, 20, 0.82) 42%, rgba(11, 14, 20, 0.45) 100%), linear-gradient(0deg, rgba(11, 14, 20, 0.65) 0%, rgba(11, 14, 20, 0.1) 55%)

  &--banner &__hero-grid
    opacity: 0.35

  &__hero-glow
    position: absolute
    width: 360px
    height: 360px
    border-radius: 50%
    filter: blur(110px)
    opacity: 0.5
    z-index: -1
    &--blue
      background: $blue
      top: -160px
      left: -90px
    &--red
      background: $red
      bottom: -190px
      right: -80px

  &__hero-inner
    position: relative
    max-width: 1140px
    margin: 0 auto
    padding: 0 24px
    > *
      animation: ev-rise .6s cubic-bezier(.2, .7, .2, 1) both
    .evento__eyebrow
      animation-delay: .04s
    .evento__title
      animation-delay: .10s
    .evento__badges
      animation-delay: .16s
    .evento__meta
      animation-delay: .22s
    .evento__desc
      animation-delay: .28s

  &__topbar
    display: flex
    align-items: center
    gap: 10px
    flex-wrap: wrap
    margin: 0 0 20px

  &__back
    display: inline-flex
    align-items: center
    gap: 8px
    background: rgba(255, 255, 255, 0.08)
    border: 1px solid rgba(255, 255, 255, 0.16)
    cursor: pointer
    color: rgba(255, 255, 255, 0.85)
    font-family: $mono
    font-size: 11px
    font-weight: 700
    letter-spacing: 0.08em
    text-transform: uppercase
    padding: 8px 14px
    border-radius: 999px
    transition: background .18s ease, color .18s ease, transform .18s ease
    &:hover
      background: rgba(255, 255, 255, 0.16)
      color: #fff
      transform: translateX(-2px)

  &__edit
    display: inline-flex
    align-items: center
    gap: 8px
    cursor: pointer
    background: rgba(40, 122, 237, 0.18)
    border: 1px solid rgba(40, 122, 237, 0.45)
    color: #fff
    font-family: $mono
    font-size: 11px
    font-weight: 700
    letter-spacing: 0.08em
    text-transform: uppercase
    padding: 8px 14px
    border-radius: 999px
    transition: background .18s ease, transform .18s ease
    &:hover
      background: rgba(40, 122, 237, 0.3)
      transform: translateY(-1px)

  &__eyebrow
    display: inline-flex
    align-items: center
    gap: 10px
    font-family: $mono
    text-transform: uppercase
    letter-spacing: 0.22em
    font-size: 12px
    font-weight: 700
    color: rgba(255, 255, 255, 0.7)
    margin: 0 0 12px

  &__eyebrow-pip
    width: 8px
    height: 8px
    border-radius: 50%
    background: linear-gradient(135deg, #5AA0FF, $red)
    box-shadow: 0 0 0 4px rgba(40, 122, 237, 0.18)

  &__title
    font-family: $display
    font-weight: 800
    text-transform: uppercase
    font-size: clamp(34px, 5vw, 56px)
    line-height: 0.96
    letter-spacing: -0.015em
    margin: 0

  &__badges
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 10px
    margin: 18px 0 0

  &__status
    font-family: $mono
    font-size: 11px
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 0.08em
    padding: 4px 12px
    border-radius: 999px
    &--live
      color: #5BD17F
      background: rgba(46, 158, 79, 0.18)
    &--scheduled
      color: #6FB0FF
      background: rgba(40, 122, 237, 0.18)
    &--done, &--idle
      color: rgba(255, 255, 255, 0.7)
      background: rgba(255, 255, 255, 0.10)

  &__when
    display: inline-flex
    align-items: center
    gap: 7px
    font-family: $mono
    font-size: 11px
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 0.06em
    padding: 4px 11px
    border-radius: 999px
    color: rgba(255, 255, 255, 0.78)
    background: rgba(255, 255, 255, 0.08)
    &--live
      color: #5BD17F
      background: rgba(46, 158, 79, 0.14)

  &__when-ic
    font-size: 10px

  &__when-dot
    width: 7px
    height: 7px
    border-radius: 50%
    background: #5BD17F
    animation: ev-pulse 1.6s ease-in-out infinite

  &__test
    font-family: $mono
    font-size: 10px
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 0.1em
    padding: 4px 10px
    border-radius: 999px
    color: #fff
    background: $red

  &__meta
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 10px 22px
    margin: 20px 0 0
    color: rgba(255, 255, 255, 0.74)
    font-size: 14px

  &__meta-item
    display: inline-flex
    align-items: center
    gap: 8px

  &__meta-ic
    color: #FF7A7A
    font-size: 13px

  &__desc
    max-width: 660px
    color: rgba(255, 255, 255, 0.66)
    font-size: 15px
    line-height: 1.6
    margin: 18px 0 0

  // ---------- NAVEGAÇÃO ----------
  &__nav-grid
    display: grid
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr))
    gap: 14px
    margin-bottom: 40px

  &__nav
    display: flex
    align-items: center
    gap: 16px
    text-align: left
    cursor: pointer
    font-family: inherit
    padding: 18px 20px
    background: $paper
    border: 1px solid $line
    border-radius: 14px
    transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease
    &:hover
      transform: translateY(-4px)
      box-shadow: 0 18px 38px rgba(11, 14, 20, 0.12)
      border-color: rgba(40, 122, 237, 0.4)
      .evento__nav-arrow
        transform: translateX(4px)
        color: $blue
    &:nth-child(even) .evento__nav-ic
      color: $red
      background: rgba(221, 52, 52, 0.10)

  &__nav-ic
    display: grid
    place-items: center
    flex-shrink: 0
    width: 46px
    height: 46px
    border-radius: 12px
    font-size: 19px
    color: $blue
    background: rgba(40, 122, 237, 0.10)

  &__nav-txt
    flex: 1 1 auto
    min-width: 0
    display: flex
    flex-direction: column
    strong
      font-family: $display
      text-transform: uppercase
      font-size: 19px
      font-weight: 700
      line-height: 1
    span
      font-family: $mono
      font-size: 11px
      letter-spacing: 0.04em
      color: $muted
      margin-top: 5px

  &__nav-arrow
    flex-shrink: 0
    font-size: 13px
    color: #C4C9D2
    transition: transform .22s ease, color .22s ease

  // ---------- SEÇÕES ----------
  &__section-title
    display: flex
    align-items: center
    gap: 12px
    font-family: $display
    text-transform: uppercase
    letter-spacing: 0.01em
    font-size: 22px
    font-weight: 700
    margin: 0 0 18px
    &::before
      content: ''
      width: 4px
      height: 20px
      border-radius: 2px
      background: linear-gradient(180deg, $blue, $red)

  // ---------- RESUMO ----------
  &__stats
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 168px), 1fr))
    gap: 14px
    margin-bottom: 40px

  &__stat
    display: flex
    flex-direction: column
    align-items: flex-start
    padding: 20px
    background: $paper
    border: 1px solid $line
    border-top: 3px solid $blue
    border-radius: 14px
    transition: transform .22s ease, box-shadow .22s ease
    &:hover
      transform: translateY(-3px)
      box-shadow: 0 14px 30px rgba(11, 14, 20, 0.10)
    &--red
      border-top-color: $red
      .evento__stat-ic
        color: $red
        background: rgba(221, 52, 52, 0.10)

  &__stat-ic
    display: grid
    place-items: center
    width: 40px
    height: 40px
    border-radius: 10px
    font-size: 17px
    color: $blue
    background: rgba(40, 122, 237, 0.10)
    margin-bottom: 16px

  &__stat-num
    font-family: $display
    font-weight: 800
    font-size: 40px
    line-height: 1
    color: $ink

  &__stat-label
    font-family: $mono
    font-size: 10px
    font-weight: 700
    letter-spacing: 0.08em
    text-transform: uppercase
    color: $muted
    margin-top: 8px

  // ---------- DURAÇÃO ESTIMADA ----------
  &__dur-tag
    display: inline-flex
    align-items: center
    gap: 6px
    margin-left: 12px
    font-family: $mono
    font-size: 9px
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 0.1em
    color: $muted
    background: $mist
    border: 1px solid $line
    border-radius: 999px
    padding: 3px 10px

  &__dur-grid
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr))
    gap: 14px
    margin-bottom: 14px

  &__dur-card
    display: flex
    flex-direction: column
    padding: 20px
    background: $paper
    border: 1px solid $line
    border-left: 4px solid $blue
    border-radius: 14px
    &--total
      border-left-color: $red
      background: linear-gradient(135deg, #FFFFFF, #FFF6F6)

  &__dur-value
    font-family: $display
    font-weight: 800
    font-size: 34px
    line-height: 1
    color: $ink

  &__dur-label
    font-family: $mono
    font-size: 10px
    font-weight: 700
    letter-spacing: 0.06em
    text-transform: uppercase
    color: $muted
    margin-top: 10px

  // ---------- POR CATEGORIA ----------
  &__panel
    background: $paper
    border: 1px solid $line
    border-radius: 14px
    overflow-x: auto
    -webkit-overflow-scrolling: touch

  &__table
    width: 100%
    min-width: 480px
    border-collapse: collapse
    font-size: 15px

    th, td
      padding: 14px 18px
      text-align: left

    th
      font-family: $mono
      font-size: 10px
      font-weight: 700
      text-transform: uppercase
      letter-spacing: 0.08em
      color: $muted
      background: $mist
      border-bottom: 1px solid $line

    tbody td
      border-bottom: 1px solid rgba(11, 14, 20, 0.06)
    tbody tr
      transition: background .15s ease
      &:hover
        background: rgba(40, 122, 237, 0.04)
    tbody td:first-child
      font-weight: 700

    .num
      text-align: right
      font-family: $display
      font-weight: 700
      font-size: 17px

    tfoot td
      font-family: $display
      font-weight: 700
      text-transform: uppercase
      border-top: 2px solid $line
      background: #FAFBFC
    tfoot .num
      font-size: 18px

  &__note
    margin-top: 14px
    font-family: $mono
    font-size: 11px
    line-height: 1.6
    color: $muted
    max-width: 720px

  // ---------- VAZIO ----------
  &__empty
    display: flex
    flex-direction: column
    align-items: center
    text-align: center
    gap: 14px
    padding: 48px 24px
    background: $paper
    border: 1px dashed rgba(11, 14, 20, 0.18)
    border-radius: 16px
    color: $muted
    .evento__empty-ic
      font-size: 34px
      color: $blue
    p
      font-size: 16px
      margin: 0

  &__empty-state
    display: flex
    flex-direction: column
    align-items: center
    text-align: center
    gap: 16px
    padding: 72px 24px
    color: $muted
    .evento__empty-ic
      font-size: 44px
      color: $blue
    p
      font-size: 18px
      margin: 0

  &__cta
    display: inline-flex
    align-items: center
    gap: 10px
    cursor: pointer
    font-family: $display
    font-weight: 600
    text-transform: uppercase
    letter-spacing: 0.06em
    font-size: 15px
    color: #fff
    background: $blue
    border: none
    border-radius: 6px
    padding: 12px 24px
    box-shadow: 0 10px 24px rgba(40, 122, 237, 0.32)
    transition: transform .18s ease, box-shadow .18s ease
    &:hover
      transform: translateY(-2px)
      box-shadow: 0 14px 30px rgba(40, 122, 237, 0.42)

// ---------- animações ----------
@keyframes ev-rise
  from
    opacity: 0
    transform: translateY(16px)
  to
    opacity: 1
    transform: none

@keyframes ev-pulse
  0%
    box-shadow: 0 0 0 0 rgba(91, 209, 127, 0.5)
  70%
    box-shadow: 0 0 0 7px rgba(91, 209, 127, 0)
  100%
    box-shadow: 0 0 0 0 rgba(91, 209, 127, 0)

// ---------- responsivo ----------
@media (max-width: 620px)
  .evento__hero
    padding: 22px 0 34px
  .evento__hero-inner
    padding: 0 18px
  .evento__wrap
    padding: 28px 18px 48px
  .evento__stat
    padding: 18px
  .evento__stat-num
    font-size: 34px
</style>
